var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';
// Import recipe builder services
import { setRecipeStatus, runRecipe } from '@automator/builder/services';
// Import i18n functions
import { __, _x } from '@wordpress/i18n';
/**
 * @todo: Once the whole project uses Lit, the dialog should be rendered in the .render method, and it should use a portal to render the dialog outside of the component's shadow DOM. We can't do the change before because the element is created again and the properties are lost.
 */
// Import Lit dependencies
import { html, render, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
let AutomatorBuilderPublishRunNow = class AutomatorBuilderPublishRunNow extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * The dialogs that are currently open
         *
         * @type {HTMLElement[]}
         * @private
         */
        this._$dialogs = [];
        /**
         * Whether the button is loading
         *
         * @type {boolean}
         * @private
         */
        this.loading = false;
    }
    /**
     * Renders the component's DOM structure based on its properties and slotted content.
     * @returns {TemplateResult} The HTML template for the run now button component.
     */
    render() {
        // Render the button only if the recipe has the "Run now" button
        if (!UncannyAutomator._recipe.miscellaneous.has_run_now) {
            // Return undefined for clarity when nothing is rendered
            return;
        }
        return html `
			<uo-button
				size="small"
				@click=${this.handleRunNow}
				?loading=${this.loading}
			>
				${escapeHTML(__('Run now', 'uncanny-automator'))}
			</uo-button>
		`;
    }
    /**
     * Invoked when the element is first updated after being connected to the DOM.
     * Checks if the recipe is editable and shows a dialog if not.
     */
    firstUpdated() {
        if (UncannyAutomator._recipe.miscellaneous.recipe_is_running) {
            this.renderRecipeRunningDialog();
        }
    }
    /**
     * Remove the dialogs when the component is disconnected from the DOM.
     *
     * @deprecated Remove this once the dialogs are rendered in the .render method
     */
    disconnectedCallback() {
        // Remove the dialogs from the list of open dialogs
        this._$dialogs.forEach($dialog => {
            // Remove the dialog from the DOM
            if (document.body.contains($dialog)) {
                $dialog.remove();
            }
        });
        // Clear the list of open dialogs
        this._$dialogs = [];
    }
    /**
     * Handles the "Run now" button click.
     * Ensures the recipe is published, then initiates the run.
     */
    async handleRunNow() {
        // If the recipe is not published, set it as published first and then run the recipe
        if (!UncannyAutomator._recipe.is_recipe_on) {
            // Set recipe as published
            try {
                await setRecipeStatus(true);
            }
            catch (e) {
                // Log error
                console.error(e);
                // Stop here
                return;
            }
        }
        // Set loading animation on the button
        this.loading = true;
        // Do call to run recipe
        runRecipe()
            .then((response) => {
            if (response.recipe_run_completed) {
                this.renderRecipeCompleteDialog();
            }
            else {
                this.renderRecipeRunningDialog();
            }
        })
            .catch(error => {
            console.error("Error running recipe:", error);
        })
            .finally(() => {
            this.loading = false;
        });
    }
    /**
     * Renders a dialog to inform the user that the recipe run has been initiated.
     */
    renderRecipeRunningDialog() {
        // Create the node that we'll use to render the dialog
        const wrapper = document.createElement('div');
        document.body.append(wrapper);
        // Add the dialog to the list of open dialogs
        this._$dialogs.push(wrapper);
        render(html `
				<uo-dialog
					open
					heading=${escapeHTML(__('The recipe run has been initiated', 'uncanny-automator'))}
					force-manual-close
				>
					<div style="margin-bottom: 15px">
						${escapeHTML(__('Actions are now running in the background and results will be available in the logs.', 'uncanny-automator'))}
					</div>

					<div style="margin-bottom: 15px">
						${escapeHTML(__("Changes cannot be made to the recipe while it's running. Progress can be monitored from the logs and actions that haven't run yet can be cancelled.", 'uncanny-automator'))}
					</div>

					<div style="margin-bottom: 15px">
						<uo-button
							color="primary"
							href="${UncannyAutomator._recipe.miscellaneous.url_logs}"
							target="_blank" >
							${escapeHTML(__('View log entry', 'uncanny-automator'))}
						</uo-button>
					</div>

					<div style="margin-bottom: 15px">
						${escapeHTML(__('You can also return to the list of recipes or create a copy of this recipe.', 'uncanny-automator'))}
					</div>

					<div>
						<uo-button
							color="secondary"
							href="${UncannyAutomator._site.automator.links.all_recipes}"
							style="margin-right: 10px"
						>
							${escapeHTML(__('Return to recipe list', 'uncanny-automator'))}
						</uo-button>

						<uo-button
							color="secondary"
							href="${UncannyAutomator._recipe.miscellaneous.url_duplicate_recipe}"
						>
						${escapeHTML(__('Duplicate this recipe', 'uncanny-automator'))}
						</uo-button>
					</div>
				</uo-dialog>
			`, wrapper);
    }
    /**
     * Renders a dialog to inform the user that the recipe run has been completed.
     */
    renderRecipeCompleteDialog() {
        // Create the node that we'll use to render the dialog
        const wrapper = document.createElement('div');
        document.body.append(wrapper);
        // Add the dialog to the list of open dialogs
        this._$dialogs.push(wrapper);
        render(html `
				<uo-dialog
					open
					heading=${escapeHTML(__('Recipe run complete', 'uncanny-automator'))}
				>
					<div style="margin-bottom: 15px">
						${escapeHTML(__('The recipe has already finished running. You can view the log entry to see the results. Once you close this dialog, you can continue editing the recipe.', 'uncanny-automator'))}
					</div>

					<uo-button
						color="primary"
						href="${UncannyAutomator._recipe.miscellaneous.url_logs}"
						target="_blank"
					>
						${escapeHTML(__('View log entry', 'uncanny-automator'))}
					</uo-button>

					<uo-button
						slot="primary-action"
						dialog-action="close"
						color="secondary"
					>
						${escapeHTML(_x('Close', 'Verb', 'uncanny-automator'))}
					</uo-button>
				</uo-dialog>
			`, wrapper);
    }
};
__decorate([
    property({ type: Boolean })
], AutomatorBuilderPublishRunNow.prototype, "loading", void 0);
AutomatorBuilderPublishRunNow = __decorate([
    customElement('uap-builder-publish-run-now-button') // eslint-disable-line require-jsdoc
], AutomatorBuilderPublishRunNow);
export { AutomatorBuilderPublishRunNow };
